import Phaser from 'phaser';

export class UIManager {
  private scene: Phaser.Scene;
  private heartText!: Phaser.GameObjects.Text;
  private coinText!: Phaser.GameObjects.Text;
  private gemText!: Phaser.GameObjects.Text;
  private timeText!: Phaser.GameObjects.Text;
  private dialogueBox!: Phaser.GameObjects.Container;
  private dialogueText!: Phaser.GameObjects.Text;
  private dialogueTimer: Phaser.Time.TimerEvent | null = null;

  private victoryContainer!: Phaser.GameObjects.Container;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.createHUD();
    this.createDialogueBox();
    this.createVictoryOverlay();
  }

  private createHUD(): void {
    const fontStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: '14px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
    };

    // Heart HP Indicator
    this.heartText = this.scene.add.text(20, 20, 'HP: ❤️❤️❤️', fontStyle).setScrollFactor(0).setDepth(100);

    // Coins Counter
    this.coinText = this.scene.add.text(20, 44, 'COINS: 🟡 0', fontStyle).setScrollFactor(0).setDepth(100);

    // Gems Counter
    this.gemText = this.scene.add.text(20, 68, 'GEMS: 💎 0', fontStyle).setScrollFactor(0).setDepth(100);

    // Elapsed Time
    this.timeText = this.scene.add.text(1100, 20, 'TIME: 00:00', fontStyle).setScrollFactor(0).setDepth(100);
  }

  private createDialogueBox(): void {
    const bg = this.scene.add.rectangle(640, 640, 800, 90, 0x0f172a, 0.9);
    bg.setStrokeStyle(3, 0x3b82f6);

    this.dialogueText = this.scene.add.text(260, 610, '', {
      fontFamily: 'Outfit, sans-serif',
      fontSize: '18px',
      color: '#f8fafc',
      wordWrap: { width: 760 },
    });

    this.dialogueBox = this.scene.add.container(0, 0, [bg, this.dialogueText]);
    this.dialogueBox.setScrollFactor(0).setDepth(200).setVisible(false);
  }

  public showDialogue(text: string, duration = 5000): void {
    this.dialogueText.setText(text);
    this.dialogueBox.setVisible(true);

    if (this.dialogueTimer) {
      this.dialogueTimer.remove();
    }

    this.dialogueTimer = this.scene.time.delayedCall(duration, () => {
      this.dialogueBox.setVisible(false);
    });
  }

  public showToast(text: string, duration = 3000, color = '#22c55e'): void {
    const toastBg = this.scene.add.rectangle(640, 60, 420, 40, 0x0f172a, 0.95);
    toastBg.setStrokeStyle(2, Phaser.Display.Color.HexStringToColor(color).color);

    const toastTxt = this.scene.add.text(640, 60, text, {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: '13px',
      color: color,
    }).setOrigin(0.5);

    const container = this.scene.add.container(0, -100, [toastBg, toastTxt]).setScrollFactor(0).setDepth(250);

    this.scene.tweens.add({
      targets: container,
      y: 0,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.time.delayedCall(duration, () => {
          this.scene.tweens.add({
            targets: container,
            y: -100,
            alpha: 0,
            duration: 300,
            onComplete: () => container.destroy(),
          });
        });
      },
    });
  }

  public updateHUD(hp: number, maxHp: number, coins: number, gems: number, totalSeconds: number): void {
    let hearts = '';
    for (let i = 0; i < maxHp; i++) {
      hearts += i < hp ? '❤️' : '🖤';
    }
    this.heartText.setText(`HP: ${hearts}`);
    this.coinText.setText(`COINS: 🟡 ${coins}`);
    this.gemText.setText(`GEMS: 💎 ${gems}`);

    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.floor(totalSeconds % 60);
    const formatted = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    this.timeText.setText(`TIME: ${formatted}`);
  }

  private createVictoryOverlay(): void {
    const overlay = this.scene.add.rectangle(640, 360, 1280, 720, 0x000000, 0.75);

    const titleText = this.scene.add.text(640, 220, 'LEVEL COMPLETE!', {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: '36px',
      color: '#facc15',
      stroke: '#000000',
      strokeThickness: 6,
    }).setOrigin(0.5);

    const subtitleText = this.scene.add.text(640, 290, 'You Conquered The Forgotten Ruins', {
      fontFamily: 'Outfit, sans-serif',
      fontSize: '22px',
      color: '#e2e8f0',
    }).setOrigin(0.5);

    const statsText = this.scene.add.text(640, 380, '', {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: '18px',
      color: '#38bdf8',
      align: 'center',
      lineSpacing: 16,
    }).setOrigin(0.5);

    const restartBtn = this.scene.add.text(640, 500, '[ PRESS SPACE TO PLAY AGAIN ]', {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: '16px',
      color: '#4ade80',
    }).setOrigin(0.5);

    this.scene.tweens.add({
      targets: restartBtn,
      alpha: 0.2,
      duration: 600,
      yoyo: true,
      repeat: -1,
    });

    this.victoryContainer = this.scene.add.container(0, 0, [overlay, titleText, subtitleText, statsText, restartBtn]);
    this.victoryContainer.setScrollFactor(0).setDepth(300).setVisible(false);
  }

  public showVictoryScreen(coins: number, gems: number, timeStr: string, onRestart: () => void): void {
    const statsText = this.victoryContainer.list[3] as Phaser.GameObjects.Text;
    statsText.setText(`FINAL SCORE\n\nCOINS: ${coins}\nGEMS: ${gems}\nTIME: ${timeStr}`);
    this.victoryContainer.setVisible(true);

    if (this.scene.input.keyboard) {
      this.scene.input.keyboard.once('keydown-SPACE', () => {
        onRestart();
      });
    }
  }
}
